import posthog from 'posthog-js';

import { getTelemetryIdentity } from './telemetryIdentity';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_API_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const SITE_LABEL = 'platform';
const PRESENCE_HEARTBEAT_INTERVAL_MS = 30_000;

const EXCLUDED_PATH_PREFIXES = ['/admin', '/internal', '/top5', '/dailypulseold'];
type UserStatus = 'anonymous' | 'free' | 'premium';
type AnalyticsUserContext = {
  userStatus?: UserStatus;
  isPremium?: boolean;
  isAuthenticated?: boolean;
};
type PageState = {
  pathname: string;
  search: string;
  context?: AnalyticsUserContext;
};
type PendingIdentifyState = {
  email: string;
  userStatus: UserStatus;
  isPremium: boolean;
  properties: Record<string, unknown>;
};

let isInitialized = false;
let hasWarnedMissingKey = false;
let pendingRoutePath: string | null = null;
let pendingPageView: PageState | null = null;
let pendingUserContext: AnalyticsUserContext = {
  userStatus: 'anonymous',
  isPremium: false,
  isAuthenticated: false,
};
let pendingIdentifyState: PendingIdentifyState | null = null;
let activePageState: PageState | null = null;
let heartbeatIntervalId: number | null = null;
let pendingTelemetryIdentity = {
  anonId: null as string | null,
  sessionId: null as string | null,
  site: SITE_LABEL,
};

const matchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const isAnalyticsExcludedPath = (pathname: string): boolean =>
  EXCLUDED_PATH_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));

const resolveStatus = (context: AnalyticsUserContext = {}): UserStatus => {
  if (context.userStatus) {
    return context.userStatus;
  }

  if (context.isPremium) {
    return 'premium';
  }

  if (context.isAuthenticated) {
    return 'free';
  }

  return 'anonymous';
};

const normalizeUserContext = (context: AnalyticsUserContext = {}): AnalyticsUserContext => {
  const userStatus = resolveStatus(context);
  return {
    userStatus,
    isPremium: userStatus === 'premium',
    isAuthenticated: userStatus !== 'anonymous',
  };
};

const resolveTelemetryContext = (): {
  anonId: string | null;
  sessionId: string | null;
  site: string;
} => {
  if (typeof window === 'undefined') {
    return pendingTelemetryIdentity;
  }

  pendingTelemetryIdentity = getTelemetryIdentity();
  return pendingTelemetryIdentity;
};

const resolveEventContext = (
  context: AnalyticsUserContext = {}
): {
  anon_id: string | null;
  is_premium: boolean;
  session_id: string | null;
  site: string;
  user_status: UserStatus;
} => {
  const normalized = resolveMergedUserContext(context);
  const telemetry = resolveTelemetryContext();
  const userStatus = resolveStatus(normalized);

  return {
    anon_id: telemetry.anonId,
    session_id: telemetry.sessionId,
    site: telemetry.site || SITE_LABEL,
    user_status: userStatus,
    is_premium: userStatus === 'premium',
  };
};

const applyUserContext = (context: AnalyticsUserContext = {}): void => {
  const normalized = normalizeUserContext(context);
  const telemetry = resolveTelemetryContext();
  pendingUserContext = normalized;

  if (!hasPostHog()) {
    return;
  }

  posthog.register({
    site: telemetry.site || SITE_LABEL,
    anon_id: telemetry.anonId,
    session_id: telemetry.sessionId,
    user_status: normalized.userStatus,
    is_premium: normalized.userStatus === 'premium',
  });
};

const flushPendingIdentify = (): void => {
  if (!hasPostHog() || !pendingIdentifyState) {
    return;
  }

  const { email, userStatus, isPremium, properties } = pendingIdentifyState;
  const telemetry = resolveTelemetryContext();
  pendingIdentifyState = null;

  posthog.identify(email, {
    email,
    site: telemetry.site || SITE_LABEL,
    anon_id: telemetry.anonId,
    session_id: telemetry.sessionId,
    user_status: userStatus,
    is_premium: isPremium,
    ...properties,
  });
};

const applyRouteCaptureState = (pathname: string): void => {
  if (isAnalyticsExcludedPath(pathname)) {
    posthog.opt_out_capturing();
    return;
  }

  posthog.opt_in_capturing();
};

const resolveMergedUserContext = (context: AnalyticsUserContext = {}): AnalyticsUserContext =>
  normalizeUserContext({
    ...pendingUserContext,
    ...context,
  });

const emitPresenceHeartbeat = (pageState: PageState): void => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.visibilityState !== 'visible') {
    return;
  }

  if (isAnalyticsExcludedPath(pageState.pathname)) {
    return;
  }

  const eventContext = resolveEventContext(pageState.context);
  posthog.capture('page_presence_heartbeat', {
    path: pageState.pathname,
    query: pageState.search,
    url: window.location.href,
    title: document.title,
    heartbeat_interval_ms: PRESENCE_HEARTBEAT_INTERVAL_MS,
    ...eventContext,
  });
};

const ensurePresenceHeartbeat = (): void => {
  if (typeof window === 'undefined' || heartbeatIntervalId !== null) {
    return;
  }

  heartbeatIntervalId = window.setInterval(() => {
    if (!activePageState || !hasPostHog()) {
      return;
    }

    emitPresenceHeartbeat(activePageState);
  }, PRESENCE_HEARTBEAT_INTERVAL_MS);
};

const emitPageView = (pathname: string, search: string, context: AnalyticsUserContext = {}): void => {
  const eventContext = resolveEventContext(context);
  posthog.capture('$pageview', {
    path: pathname,
    query: search,
    url: window.location.href,
    title: document.title,
    ...eventContext,
  });

  if (matchesPrefix(pathname, '/dashboards')) {
    posthog.capture('dashboard_viewed', {
      dashboard_path: pathname,
      ...eventContext,
    });
  }
};

export const initAnalytics = (): void => {
  if (typeof window === 'undefined' || isInitialized) {
    return;
  }

  if (!POSTHOG_KEY) {
    if (!hasWarnedMissingKey) {
      hasWarnedMissingKey = true;
      console.warn('PostHog is disabled because REACT_APP_POSTHOG_API_KEY is not set.');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: false,
    person_profiles: 'identified_only',
  });

  isInitialized = true;
  applyUserContext(pendingUserContext);
  flushPendingIdentify();

  if (pendingRoutePath) {
    applyRouteCaptureState(pendingRoutePath);
  }

  ensurePresenceHeartbeat();

  if (typeof document !== 'undefined') {
    const emitPresenceForActivePage = (): void => {
      if (!activePageState || !hasPostHog()) {
        return;
      }

      emitPresenceHeartbeat(activePageState);
    };

    document.addEventListener('visibilitychange', emitPresenceForActivePage);
    window.addEventListener('focus', emitPresenceForActivePage);
  }

  if (pendingPageView && !isAnalyticsExcludedPath(pendingPageView.pathname)) {
    emitPageView(pendingPageView.pathname, pendingPageView.search, pendingPageView.context);
    activePageState = pendingPageView;
    emitPresenceHeartbeat(pendingPageView);
  }
};

const hasPostHog = (): boolean => isInitialized && typeof window !== 'undefined';

export const syncAnalyticsCaptureForRoute = (pathname: string): void => {
  pendingRoutePath = pathname;

  if (!hasPostHog()) {
    return;
  }

  applyRouteCaptureState(pathname);
};

export const capturePageView = (
  pathname: string,
  search: string,
  context: AnalyticsUserContext = {}
): void => {
  pendingPageView = { pathname, search, context };
  activePageState = pendingPageView;

  if (isAnalyticsExcludedPath(pathname)) {
    activePageState = null;
    return;
  }

  if (!hasPostHog()) {
    return;
  }

  emitPageView(pathname, search, context);
  emitPresenceHeartbeat(pendingPageView);
};

export const captureEvent = (eventName: string, properties: Record<string, unknown> = {}): void => {
  if (!hasPostHog()) {
    return;
  }

  posthog.capture(eventName, {
    ...resolveEventContext(pendingUserContext),
    ...properties,
  });
};

export const identifyAnalyticsUser = (
  email: string,
  properties: Record<string, unknown> = {}
): void => {
  const isPremium =
    properties.is_premium === true || properties.isPremium === true;
  const userStatus: UserStatus = isPremium ? 'premium' : 'free';

  pendingIdentifyState = {
    email,
    userStatus,
    isPremium,
    properties,
  };

  applyUserContext({
    userStatus,
    isPremium,
    isAuthenticated: true,
  });

  flushPendingIdentify();
};

export const resetAnalyticsUser = (): void => {
  pendingIdentifyState = null;

  if (hasPostHog()) {
    posthog.reset();
  }

  applyUserContext({
    userStatus: 'anonymous',
    isPremium: false,
    isAuthenticated: false,
  });
};
